<template>
  <Modal :style="computedWindowSize"
         :visible="visible"
         class="p-fluid"
         @close="close">
    <template #header>
      <div class="p-dialog-title">{{ activeCustomer?.id ? $t('customerEditModalTitle') : $t('customerCreateModalTitle') }}</div>
    </template>

    <Spinner v-show="dataIsSending"/>

    <div v-show="!dataIsSending">
      <!-- TYPE SWITCH -->
      <div class="p-formgrid p-grid" v-show="!activeCustomer?.id && !forceIndividual">
        <ul class="p-col-12 p-md-12 p-d-flex p-flex-wrap">
          <li class="p-field-radiobutton p-mr-3">
            <RadioButton :id="'individual_checkbox' + (isSecondModal ? '--second-modal' : '')"
                         value="Individual"
                         v-model="customerType"/>
            <label :for="'individual_checkbox' + (isSecondModal ? '--second-modal' : '')"
                   class="radio-button-label">
              {{ $t('Individual') }}
            </label>
          </li>

          <li class="p-field-radiobutton">
            <RadioButton :id="'company_checkbox' + (isSecondModal ? '--second-modal' : '')"
                         value="Company"
                         v-model="customerType"/>
            <label :for="'company_checkbox' + (isSecondModal ? '--second-modal' : '')"
                   class="radio-button-label">
              {{ $t('Company') }}
            </label>
          </li>
        </ul>
      </div>

      <!-- ========================= -->
      <!-- COMPANY TEMPLATE           -->
      <!-- ========================= -->
      <template v-if="customerType === 'Company'">
        <div class="p-formgrid p-grid p-pt-3">
          <div class="p-field p-col-12 p-sm-12 p-md-6 p-lg-6 p-mb-5" v-if="$store.getters.isLocationInEstonia">
            <EECompanyRegisterDropdown v-model="selectedCompany"
                                       :disabled="dataIsSending"
                                       :resetOnClose="true"
                                       :visible="visible"
                                       :showClear="true"
                                       ref="selectedCompanyDropdown"
                                       @change="changeCompany"
                                       inputId="customer-modal-ee-company-dropdown"/>
          </div>

          <div class="p-field p-col-12 p-sm-12 p-mb-5" :class="$store.getters.isLocationInEstonia ? 'p-md-6 p-lg-6' : 'p-md-12 p-lg-12'">
            <div class="p-float-label">
              <InputText id="customer_company_name"
                         :disabled="companyData.company_name && selectedCompany"
                         :class="{'p-invalid' : submitted && !companyData.company_name}"
                         v-model.trim="companyData.company_name"
                         autocomplete="new-password"/>
              <label for="customer_company_name">
                {{ $t('Company name') }}<span class="warning-color">*</span>
              </label>
            </div>
            <small class="p-invalid" v-if="submitted && !companyData.company_name">{{ $t('Required field') }}</small>
          </div>

          <div class="p-field p-col-12 p-sm-12 p-md-6 p-lg-6 p-mb-5">
            <div class="p-float-label">
              <InputText id="customer_reg_number"
                         :class="{'p-invalid' : submitted && (!companyData.reg_number || regNumberIsTaken)}"
                         v-model.trim="companyData.reg_number"
                         :disabled="companyData.reg_number && selectedCompany"
                         @input="checkTakenRegNumberMatches"
                         @blur="checkTakenRegNumberMatches"
                         @paste="checkTakenRegNumberMatches"
                         autocomplete="new-password"/>
              <label for="customer_reg_number">
                {{ $t('Reg. number') }}<span class="warning-color">*</span>
              </label>
            </div>
            <small class="p-invalid" v-if="submitted && !companyData.reg_number">{{ $t('Required field') }}</small>
            <small class="p-invalid" v-else-if="submitted && companyData.reg_number && regNumberIsTaken">
              {{ $t('registrationNumberTaken') }}
            </small>
          </div>

          <div class="p-field p-col-12 p-sm-12 p-md-6 p-lg-6 p-mb-5">
            <div class="p-float-label">
              <InputText id="customer_vat_number"
                         :disabled="selectedCompany"
                         v-model.trim="companyData.vat_number"
                         autocomplete="new-password"/>
              <label for="customer_vat_number">{{ $t('VAT number') }}</label>
            </div>
          </div>

          <div class="p-field p-col-12 p-sm-12 p-md-6 p-lg-6" :class="[$store.getters.isAdminOrHigher ? 'p-mb-5' : 'p-mb-3']">
            <EmailInput v-model="companyData.invoice_email"
                        v-model:isInvalid="invoiceEmailInvalid"
                        :submitted="submitted"
                        label="Invoice Email"
                        inputId="invoice-email"/>
          </div>

          <div v-show="$store.getters.isAdminOrHigher"
               class="p-field p-col-12 p-sm-12 p-md-6 p-lg-6 p-mb-5">
            <div class="p-float-label">
              <InputNumber id="customer_days_to_pay"
                           :locale="computedNumberInputLocale"
                           v-model="companyData.days_to_pay"
                           :min="0"
                           :max="60"
                           showButtons
                           autocomplete="new-password"/>
              <label for="customer_days_to_pay">{{ $t('Days to pay') }}</label>
            </div>
          </div>


          <!-- ✅ Discounts: for Company only -->
          <div v-show="$store.getters.isAdminOrHigher"
               class="p-field p-col-12 p-sm-12 p-md-6 p-lg-6 p-mb-5">
            <div class="p-float-label p-input-icon-right">
              <InputNumber id="company_services_discount"
                           :locale="computedNumberInputLocale"
                           v-model="companyData.services_discount"
                           mode="decimal"
                           suffix="%"
                           :min="0"
                           :max="100"
                           showButtons
                           :minFractionDigits="2"
                           :maxFractionDigits="2"
                           autocomplete="new-password"/>
              <label for="company_services_discount">{{ $t('Services discount') }}</label>
            </div>
          </div>

          <div v-show="$store.getters.isAdminOrHigher"
               class="p-field p-col-12 p-sm-12 p-md-6 p-lg-6 p-mb-5">
            <div class="p-float-label p-input-icon-right">
              <InputNumber id="company_products_discount"
                           :locale="computedNumberInputLocale"
                           v-model="companyData.products_discount"
                           mode="decimal"
                           suffix="%"
                           :min="0"
                           :max="100"
                           showButtons
                           :minFractionDigits="2"
                           :maxFractionDigits="2"
                           autocomplete="new-password"/>
              <label for="company_products_discount">{{ $t('Products discount') }}</label>
            </div>
          </div>


          <!--          !!!!Temporary don't show-->
          <div v-show="$store.getters.isAdminOrHigher && false"
               class="p-field p-col-12 p-sm-12 p-md-6 p-lg-6 p-mb-3">
            <div class="p-float-label">
              <InputNumber id="customer_credit_limit"
                           :locale="computedNumberInputLocale"
                           v-model="companyData.credit_limit"
                           mode="decimal"
                           :min="0"
                           showButtons
                           :minFractionDigits="2"
                           :maxFractionDigits="2"
                           autocomplete="new-password"/>
              <label for="customer_credit_limit">{{ $t('Credit limit') }}</label>
            </div>
          </div>

          <div class="p-field p-col-12 p-sm-12 p-md-12 p-lg-12" :class="{'p-mb-5': showCommentField}">
            <AddressFieldset :userAddressInFlight="userAddressInFlight"
                             :modalLayer="modalLayer"
                             :user="companyData"
                             @update-item="updateCompanyAddress"
                             @update-items="updateItems"/>

          </div>
        </div>


        <div class="section-divider p-mt-3" v-if="showRepresentativeBlock">
          <div class="p-d-flex p-ai-center">
            <Checkbox id="add-contact-person-checkbox" :binary="true" v-model="addContactPerson"/>
            <label for="add-contact-person-checkbox" class="pointer p-ml-2">
              {{ $t('addContactPersonLabel') }}
            </label>
          </div>
          <hr class="section-divider__hr">
        </div>


        <div v-if="showRepresentativeBlock && addContactPerson" class="p-formgrid p-grid p-mt-5">
          <div class="p-field p-col-12 p-sm-12 p-md-6 p-lg-6 p-mb-5">
            <div class="p-float-label p-input-icon-right">
              <i class="pi pi-user"/>
              <!--                :class="{'p-invalid' : submitted && !representativeData.first_name}"-->
              <InputText id="rep-first-name"
                         @input="onNameInput('firstName', $event)"
                         @keydown="onNameKeyDown"
                         @paste="onNamePaste"
                         :class="{'p-invalid' : submitted && addContactPerson && !representativeData.first_name}"
                         v-model.trim="representativeData.first_name"
                         autocomplete="new-password"/>
              <label for="rep-first-name">{{ $t('First name') }}<span class="warning-color">*</span></label>
            </div>
            <small class="p-invalid" v-if="submitted && addContactPerson && !representativeData.first_name">
              {{ $t('Required field') }}
            </small>
          </div>

          <div class="p-field p-col-12 p-sm-12 p-md-6 p-lg-6 p-mb-5">
            <div class="p-float-label p-input-icon-right">
              <i class="pi pi-user"/>
              <InputText id="rep-last-name"
                         @input="onNameInput('lastName', $event)"
                         @keydown="onNameKeyDown"
                         @paste="onNamePaste"
                         v-model.trim="representativeData.last_name"
                         autocomplete="new-password"/>
              <label for="rep-last-name">{{ $t('Last name') }}</label>
            </div>
          </div>

          <div class="p-field p-col-12 p-sm-12 p-md-6 p-lg-6 p-mb-5">
            <PhoneInput v-model:phoneCountry="representativeData.phone_country"
                        v-model:phoneCode="representativeData.phone_code"
                        v-model:phoneNumber="representativeData.phone_number"
                        v-model:isInvalid="phoneInvalid"
                        :required="addContactPerson"
                        :visible="visible"
                        :modalData="representativeData"
                        :submitted="submitted && addContactPerson"/>
          </div>

          <div class="p-field p-col-12 p-sm-12 p-md-6 p-lg-6 p-mb-5">
            <EmailInput v-model="representativeData.email"
                        v-model:isInvalid="emailInvalid"
                        :submitted="submitted"
                        inputId="rep-email"/>
          </div>

          <div class="p-field p-col-12 p-sm-12 p-md-6 p-lg-6" :class="{'p-mb-5':showCommentField}">
            <div class="p-float-label">
              <CustomDropdown id="customer-language"
                              :class="{'p-invalid' : submitted && addContactPerson && !selectedLanguage}"
                              v-model="selectedLanguage"
                              @change="changeLanguage"
                              :options="languageValues"
                              :clearSearchData="!visible"
                              data-key="value"
                              :filter="true"
                              :filterFields="['name', 'value', 'label']"
                              :showClear="false">
                <template #value="slotProps">
                  <div class="country-item country-item-value" v-if="slotProps.value">
                    {{ formatLanguageName(slotProps.value) }}
                  </div>
                  <span v-else style="visibility: hidden">.</span>
                </template>
                <template #option="slotProps">
                  <div>{{ formatLanguageName(slotProps.option) }}</div>
                </template>
              </CustomDropdown>
              <label for="customer-language">{{ $t('Language') }}<span class="warning-color">*</span></label>
            </div>
            <small class="p-invalid" v-if="submitted && addContactPerson && !selectedLanguage">
              {{ $t('Required field') }}
            </small>
          </div>
        </div>



      </template>

      <!-- ========================= -->
      <!-- INDIVIDUAL TEMPLATE        -->
      <!-- ========================= -->
      <template v-else>
        <div class="p-formgrid p-grid p-pt-3">
          <div class="p-field p-col-12 p-sm-12 p-md-6 p-lg-6 p-mb-5">
            <div class="p-float-label p-input-icon-right">
              <i class="pi pi-user" />
              <InputText id="customer-first-name"
                         @input="onNameInput('firstName', $event)"
                         @keydown="onNameKeyDown"
                         @paste="onNamePaste"
                         :class="{'p-invalid' : submitted && !representativeData.first_name}"
                         v-model.trim="representativeData.first_name"
                         autocomplete="new-password"/>
              <label for="customer-first-name">{{ $t('First name') }}<span class="warning-color">*</span></label>
            </div>
            <small class="p-invalid" v-if="submitted && !representativeData.first_name">{{ $t('Required field') }}</small>
          </div>

          <div class="p-field p-col-12 p-sm-12 p-md-6 p-lg-6 p-mb-5">
            <div class="p-float-label p-input-icon-right">
              <i class="pi pi-user" />
              <InputText id="customer-last-name"
                         @input="onNameInput('lastName', $event)"
                         @keydown="onNameKeyDown"
                         @paste="onNamePaste"
                         v-model.trim="representativeData.last_name"
                         autocomplete="new-password"/>
              <label for="customer-last-name">{{ $t('Last name') }}</label>
            </div>
          </div>

          <div class="p-field p-col-12 p-sm-12 p-md-6 p-lg-6 p-mb-5">
            <!--            :required="true"-->
            <!--            :submitted="submitted"-->
            <PhoneInput v-model:phoneCountry="representativeData.phone_country"
                        v-model:phoneCode="representativeData.phone_code"
                        v-model:phoneNumber="representativeData.phone_number"
                        v-model:isInvalid="phoneInvalid"
                        :required="true"
                        :visible="visible"
                        :modalData="representativeData"
                        :submitted="submitted"/>
          </div>

          <div class="p-field p-col-12 p-sm-12 p-md-6 p-lg-6 p-mb-5">
            <EmailInput v-model="representativeData.email"
                        v-model:isInvalid="emailInvalid"
                        :submitted="submitted"
                        inputId="customer-email"/>
          </div>

          <!-- ✅ Discounts: for Individual only -->
          <div v-show="$store.getters.isAdminOrHigher"
               class="p-field p-col-12 p-sm-12 p-md-6 p-lg-6 p-mb-5">
            <div class="p-float-label p-input-icon-right">
              <InputNumber id="individual_services_discount"
                           :locale="computedNumberInputLocale"
                           v-model="representativeData.services_discount"
                           mode="decimal"
                           suffix="%"
                           :min="0"
                           :max="100"
                           showButtons
                           :minFractionDigits="2"
                           :maxFractionDigits="2"
                           autocomplete="new-password"/>
              <label for="individual_services_discount">{{ $t('Services discount') }}</label>
            </div>
          </div>

          <div v-show="$store.getters.isAdminOrHigher"
               class="p-field p-col-12 p-sm-12 p-md-6 p-lg-6 p-mb-5">
            <div class="p-float-label p-input-icon-right">
              <InputNumber id="individual_products_discount"
                           :locale="computedNumberInputLocale"
                           v-model="representativeData.products_discount"
                           mode="decimal"
                           suffix="%"
                           :min="0"
                           :max="100"
                           showButtons
                           :minFractionDigits="2"
                           :maxFractionDigits="2"
                           autocomplete="new-password"/>
              <label for="individual_products_discount">{{ $t('Products discount') }}</label>
            </div>
          </div>

          <div class="p-field p-col-12 p-sm-12 p-md-6 p-lg-6 p-mb-5">
            <div class="p-float-label">
              <!--              :class="{'p-invalid' : submitted && !selectedLanguage}"-->
              <CustomDropdown id="customer-language"
                              :class="{'p-invalid' : submitted && !selectedLanguage}"
                              v-model="selectedLanguage"
                              @change="changeLanguage"
                              :options="languageValues"
                              :clearSearchData="!visible"
                              data-key="value"
                              :filter="true"
                              :filterFields="['value', 'label']"
                              optionLabel="value"
                              :showClear="false">
                <template #value="slotProps">
                  <div class="country-item country-item-value" v-if="slotProps.value">
                    {{ formatLanguageName(slotProps.value) }}
                  </div>
                  <span v-else style="visibility: hidden">.</span>
                </template>
                <template #option="slotProps">
                  <div>{{ formatLanguageName(slotProps.option) }}</div>
                </template>
              </CustomDropdown>
              <label for="customer-language">{{ $t('Language') }}<span class="warning-color">*</span></label>
            </div>
            <!--            <small class="p-invalid" v-if="submitted && !selectedLanguage">{{ $t('Required field') }}</small>-->
            <small class="p-invalid" v-if="submitted && !selectedLanguage">
              {{ $t('Required field') }}
            </small>
          </div>

        </div>


      </template>

      <!-- ========================= -->
      <!-- COMMON FIELDS (BOTH)       -->
      <!-- ========================= -->
      <div class="p-formgrid p-grid" v-if="showCommentField">
        <div class="p-field p-col-12 p-sm-12 p-md-12 p-lg-12">
          <LimitedCharsTextarea :submitted="submitted"
                                :rows="2"
                                v-model="activeCustomer.comment"
                                :maxChars="commentMaxChars"
                                :autocomplete="'new-password'"
                                :id="'customer-notes'"
                                :label="$t('Notes')"/>
        </div>
      </div>
    </div>

    <template #footer>
      <Button :label="$t('Cancel')" icon="pi pi-times" class="p-button-text" @click="close"/>
      <Button :label="$t('Save')"
              :disabled="dataIsSending || disableSaveButton"
              :loading="dataIsSending"
              icon="pi pi-check"
              class="p-button-text"
              @click="saveItem"/>
    </template>
  </Modal>

  <ItemExistsModal :visible="itemExistsModal"
                   :itemId="activeCustomer?.id ? +activeCustomer.id : null"
                   type="customer"
                   :items="foundItems"
                   @select-existing="selectExistingItem"
                   @close="closeItemExistsModal"/>
</template>

<script>
import httpClient from '@/services/http.services'
import settings from '@/settings'
import constants from '@/constants'
import AddressFieldset from "@/pages/users/components/AddressFieldset";
import formatMixins from "@/mixins/formatMixins";
import overlayVisibilityMixins from "@/mixins/overlayVisibilityMixins";
import showErrorsMixins from "@/mixins/showErrorsMixins";
import inputsFormatMixins from "@/mixins/inputsFormatMixins";
import PhoneInput from "@/components/PhoneInput";
import EmailInput from "@/components/EmailInput";
import EECompanyRegisterDropdown from "@/components/EECompanyRegisterDropdown";
import ItemExistsModal from "@/components/ItemExistsModal.vue";
import foundModalItemsMixin from "@/mixins/foundModalItemsMixin";

export default {
  mixins: [ showErrorsMixins, formatMixins, overlayVisibilityMixins, inputsFormatMixins, foundModalItemsMixin ],
  components: { ItemExistsModal, AddressFieldset, PhoneInput, EmailInput, EECompanyRegisterDropdown },
  emits: ['close', 'update-item', 'update-items'],
  name: 'CustomerModal',
  props: {
    item: {
      type: Object,
      default: null
    },
    visible: {
      type: Boolean,
      default: false
    },
    modalLayer: {
      type: Number,
      default: 1
    },
    isSecondModal: {
      type: Boolean,
      default: false
    },
    forceIndividual: {
      type: Boolean,
      default: false
    },
  },
  data() {
    return {
      addContactPerson: true,
      emailInvalid: false,
      invoiceEmailInvalid: false,
      phoneInvalid: false,
      userAddressInFlight: false,

      selectedCompany: null,
      selectedCustomer: null,

      commentMaxChars: 500,
      regNumberIsTaken: false,
      takenRegNumber: null,

      dateIsValid: false,
      settings,
      customerType: 'Individual',
      submitted: false,

      companyData: {},
      representativeData: {},

      selectedLanguage: null,
      languageValues: settings.languages,

      savePassword: false,
      constants,
      disableSaveButton: false,
      dataIsSending: false,
    }
  },
  watch: {
    item(value) {
      this.createItemData(value)
    },
    visible() {
      if (!this.visible) {
        this.disableSaveButton = false
        this.dataIsSending = false

        this.companyData = {}
        this.representativeData = {}

        this.selectedLanguage = null
        this.submitted = false
        this.savePassword = false
        this.customerType = 'Individual'
        this.regNumberIsTaken = false
        this.takenRegNumber = null
        this.selectedCompany = null
        this.emailInvalid = false
        this.invoiceEmailInvalid = false
        this.phoneInvalid = false

        this.addContactPerson = true
      }
    },
  },
  computed: {
    showCommentField() {
      return !!this.activeCustomer?.id || this.customerType === 'Individual'
    },
    isCompany() { return this.customerType === 'Company' },
    isEdit() {
      return !!this.activeCustomer?.id
    },
    showRepresentativeBlock() {
      return this.isCompany && !this.isEdit
    },
    activeCustomer() {
      return this.isCompany ? this.companyData : this.representativeData
    },
    computedDefaultLanguage() {
      return settings.languages.find(language => language.isDefault) ?? null
    },
    computedLocationDefaultLanguage() {
      const location = this.$store.getters.locationData ?? {}

      if (location.language) {
        const found = settings.languages.find(item => item.value === location.language)
        if (found) return found
      }

      return this.computedDefaultLanguage
    },
    computedWindowSize() {
      if (this.$store.state.mobileLayout) {
        return {position: 'absolute', top: this.modalLayer === 2 ? '15px' : 0, width: '95%', maxWidth: '550px', marginTop: '10px'}
      } else if (!this.$store.state.mobileLayout && this.$store.state.tabletLayout) {
        return {position: 'absolute', top: this.modalLayer === 2 ? '15px' : 0, width: '550px', marginTop: '15px'}
      }
      return {position: 'absolute', top: this.modalLayer === 2 ? '15px' : 0, width: '550px'}
    },
  },
  methods: {
    formatLanguageName(language) {
      if (!language) return ''
      return `${String(language.value || '').toUpperCase()} — ${language.label}`
    },
    selectExistingItem(customerData) {
      if (!customerData) return
      this.submitted = false
      this.createItemData(customerData)
      this.addContactPerson = false
    },
    changeCompany() {
      const addressId = this.companyData.address?.id ?? null
      if (!this.selectedCompany) {
        this.companyData.company_name = null
        this.companyData.vat_number = null
        this.companyData.reg_number = null
        this.companyData.address = null
      } else {
        this.companyData.company_name = this.selectedCompany.name
        this.companyData.vat_number = this.selectedCompany.vat
        this.companyData.reg_number = this.selectedCompany.registry_code
        this.companyData.address = {
          id: addressId,
          address: this.selectedCompany.address,
          address2: this.selectedCompany.address2,
          city: this.selectedCompany.city,
          state: this.selectedCompany.state,
          postcode: this.selectedCompany.postcode,
          country_id: this.selectedCompany.country_id ? Number(this.selectedCompany.country_id) : null,
          country: this.selectedCompany.country ?? null,
          country_name: this.selectedCompany.country?.name_en ?? this.selectedCompany.country_name ?? null,
        }
      }
    },

    async getCompanyAddress() {
      const userId = this.companyData?.id
      if (!userId || this.userAddressInFlight || !this.$store.getters.userCanGetData) return
      this.userAddressInFlight = true
      try {
        const { status, data } = await httpClient(`user/get-address?userId=${userId}`)
        this.companyData.address = (status === 200 && data) ? data : null
      } catch (err) {
        this.showError(err)
      } finally {
        this.userAddressInFlight = false
      }
    },

    updateCompanyAddress(newAddress, updateItems) {
      if (!this.companyData) return
      this.companyData.address = newAddress ? { ...newAddress } : null
      this.$emit('update-item', this.companyData, updateItems)
    },
    updateItems() {
      this.$emit('update-items', this.activeCustomer.id)
    },

    checkTakenRegNumberMatches() {
      if (this.customerType === 'Company') {
        this.regNumberIsTaken = this.companyData.reg_number === this.takenRegNumber
      } else {
        this.regNumberIsTaken = false
      }
    },

    changeLanguage() {
      const language = this.selectedLanguage?.value ?? null
      const flagCode = this.selectedLanguage?.flagCode ?? null

      if (this.customerType === 'Company') {
        this.companyData.language = language
        this.companyData.flag_code = flagCode

        this.representativeData.language = language
        this.representativeData.flag_code = flagCode
      } else {
        this.representativeData.language = language
        this.representativeData.flag_code = flagCode
      }
    },

    createItemData(value) {
      if (!value) {
        this.companyData = {}
        this.representativeData = {}
        this.selectedCompany = null
        this.selectedCustomer = null
        this.selectedLanguage = this.computedLocationDefaultLanguage
        this.customerType = this.forceIndividual ? 'Individual' : 'Individual'
        return
      }

      if (this.forceIndividual) {
        this.customerType = 'Individual'
      } else if (value.type) {
        this.customerType = +value.type === constants.userTypes.legal_entity ? 'Company' : 'Individual'
      } else {
        const def = this.$store.getters.locationData?.default_customer_type
        this.customerType = def && +def === 2 ? 'Company' : 'Individual'
      }

      if (value.language) {
        this.selectedLanguage = settings.languages.find((item) => item.value === value.language) ?? this.computedDefaultLanguage
      } else {
        this.selectedLanguage = this.computedLocationDefaultLanguage
      }

      // ===== individual =====
      if (this.customerType === 'Individual') {
        this.representativeData = {
          ...value,
          type: constants.userTypes.individual,
          services_discount: value.services_discount ? +value.services_discount : null,
          products_discount: value.products_discount ? +value.products_discount : null,
          credit_limit: null,
          days_to_pay: null,
        }

        if (!value.id && !value.first_name) {
          this.representativeData.first_name = this.$store.getters.locationData.default_customer_name ?? ''
        }

        if (this.selectedLanguage) {
          this.representativeData.flag_code = this.selectedLanguage.flagCode
          this.representativeData.language = this.selectedLanguage.value
        }

        this.selectedCustomer = { ...this.representativeData } // permissions mixin

        if (!value.id && value.searchedPhoneNumber) {
          this.representativeData.phone_number = value.searchedPhoneNumber
        }

        return
      }

      // ===== company =====
      this.companyData = {
        ...value,
        type: constants.userTypes.legal_entity,
        invoice_email: value.email ?? null,
        credit_limit: value.credit_limit ? +value.credit_limit : null,
        days_to_pay: value.days_to_pay ? +value.days_to_pay : null,
        services_discount: value.services_discount ? +value.services_discount : null,
        products_discount: value.products_discount ? +value.products_discount : null,
      }

      // representative (from same payload, if backend already sends them)
      this.representativeData = {
        id: value.person_id ?? value.person_user_id ?? null,
        // first_name: value.first_name ?? (this.$store.getters.locationData.default_customer_name ?? ''),
        first_name: value.first_name ?? (!value.id ? (this.$store.getters.locationData.default_customer_name ?? '') : null),
        last_name: value.last_name ?? null,
        email: value.email ?? null,
        phone_country: value.phone_country ?? null,
        phone_code: value.phone_code ?? null,
        phone_number: value.phone_number ?? null,
        // birthdate: value.birthdate ? new Date(value.birthdate * 1000) : null,
      }

      if (!value.id && value.searchedPhoneNumber) {
        this.representativeData.phone_number = value.searchedPhoneNumber
      }

      // this.checkDateIsValid()

      if (this.selectedLanguage) {
        this.companyData.flag_code = this.selectedLanguage.flagCode
        this.companyData.language = this.selectedLanguage.value
        this.representativeData.flag_code = this.selectedLanguage.flagCode
        this.representativeData.language = this.selectedLanguage.value
      }

      this.selectedCustomer = { ...this.companyData } // permissions mixin
      this.getCompanyAddress()
    },

    async saveItem() {
      if (this.dataIsSending) return
      this.submitted = true

      const isCompany = this.customerType === 'Company'
      const isEditCompany = isCompany && !!this.companyData?.id

      const sendRepresentative = !isCompany
          ? true
          : (!isEditCompany && this.addContactPerson)


      if (this.activeCustomer.comment && this.activeCustomer.comment.length > this.commentMaxChars) {
        return false
      }

      if (!isCompany && !this.selectedLanguage) return false
      if (isCompany && sendRepresentative && !this.selectedLanguage) return false

      if (isCompany && this.invoiceEmailInvalid) return

      if (
          (!isCompany && (this.phoneInvalid || this.emailInvalid)) ||
          (isCompany && sendRepresentative && (this.phoneInvalid || this.emailInvalid))
      ) {
        return
      }

      if (!isCompany) {
        if (!this.representativeData.first_name) return
      } else {
        if (!this.companyData.company_name || !this.companyData.reg_number) return
        if (sendRepresentative && !this.representativeData.first_name) return
      }

      this.disableSaveButton = true
      this.dataIsSending = true

      const repSavePhone = !!(this.representativeData.phone_code && this.representativeData.phone_number)

      const user = {
        id: isCompany ? (this.companyData?.id ?? null) : (this.representativeData?.id ?? null),
        type: isCompany ? constants.userTypes.legal_entity : constants.userTypes.individual,

        // company fields
        company_name: isCompany ? (this.companyData.company_name ?? null) : null,
        reg_number: isCompany ? (this.companyData.reg_number ?? null) : null,
        vat_number: isCompany ? (this.companyData.vat_number ?? null) : null,
        invoice_email: isCompany && this.companyData.invoice_email ? this.strToLowerCase(this.companyData.invoice_email) : null,
        days_to_pay: isCompany && this.companyData.days_to_pay ? this.companyData.days_to_pay : null,
        credit_limit: isCompany && this.companyData.credit_limit ? this.companyData.credit_limit : null,
        address: isCompany && this.companyData.address ? this.companyData.address : null,

        first_name: sendRepresentative && this.representativeData.first_name ? this.formatName(this.representativeData.first_name) : null,
        last_name: sendRepresentative && this.representativeData.last_name ? this.formatName(this.representativeData.last_name) : null,
        email: sendRepresentative && this.representativeData.email ? this.strToLowerCase(this.representativeData.email) : null,
        phone_country: sendRepresentative && repSavePhone ? (this.representativeData.phone_country ?? null) : null,
        phone_code: sendRepresentative && repSavePhone ? (this.representativeData.phone_code ?? null) : null,
        phone_number: sendRepresentative && repSavePhone ? (this.representativeData.phone_number ?? null) : null,

        comment: this.activeCustomer.comment ? this.activeCustomer.comment : null,
        language: this.selectedLanguage?.value,
        flag_code: this.selectedLanguage?.flagCode,

        services_discount: (() => {
          const v = isCompany ? this.companyData.services_discount : this.representativeData.services_discount
          return +v === 0 ? null : (v ?? null)
        })(),

        products_discount: (() => {
          const v = isCompany ? this.companyData.products_discount : this.representativeData.products_discount
          return +v === 0 ? null : (v ?? null)
        })(),

        isNew: false,
      }

      if (sendRepresentative && user.phone_code && user.phone_number) {
        user.full_phone_number = String(user.phone_code) + String(user.phone_number)
      }

      if (!sendRepresentative) {
        delete user.first_name
        delete user.last_name
        delete user.email
        delete user.phone_country
        delete user.phone_code
        delete user.phone_number
        delete user.full_phone_number
        // delete user.birthdate
      }

      if (this.savePassword && this.activeCustomer.password) {
        user.password = this.activeCustomer.password
      }

      // update
      if (this.activeCustomer?.id) {
        // user.role = this.activeCustomer.role
        user.customerStatus = this.activeCustomer.customerStatus

        try {
          const { status, data } = await httpClient.post(`user/update`, user)

          if (status === 200 && data?.success) {
            let payload = null

            if (this.customerType === 'Company') {
              payload = {
                mode: 'company',
                customer: {
                  ...this.companyData,
                  id: this.companyData?.id,
                  type: constants.userTypes.legal_entity,
                  language: this.selectedLanguage?.value,
                  flag_code: this.selectedLanguage?.flagCode,
                },
                representative: null,
              }
            } else {
              payload = {
                mode: 'individual',
                customer: {
                  ...this.representativeData,
                  id: this.representativeData?.id,
                  type: constants.userTypes.individual,
                  language: this.selectedLanguage?.value,
                  flag_code: this.selectedLanguage?.flagCode,
                },
              }
            }

            this.$emit('update-items', this.activeCustomer.id)
            this.$emit('update-item', payload)

            this.$toast.add({severity: 'success', detail: this.$t('Data updated'), life: this.settings.toastLife})

            this.close()
          } else if (data.found_items) {
            this.foundItems = data.found_items
            this.openItemExistsModal()
          } else if (data?.error) {
            this.$toast.add({severity: 'error', summary: data.error.summary, detail: data.error.detail, life: this.settings.toastLife})
          } else {
            this.showUnknownError()
          }
        } catch (err) {
          this.showError(err)
        } finally {
          this.dataIsSending = false
          this.disableSaveButton = false
        }
        return
      }

      // create
      // user.role = constants.userRoles.customer
      try {
        const { status, data } = await httpClient.post(`user/create`, user)

        if (status === 201 && data?.success) {
          let payload = null
          let emittedId = null

          if (this.customerType === 'Company') {
            payload = {
              mode: 'company',
              customer: data.customer ?? null,
              representative: data.representative ?? null,
            }
            emittedId = data.customer?.id ?? null
          } else {
            payload = {
              mode: 'individual',
              customer: data.customer ?? null,
            }
            emittedId = data.customer?.id ?? null
          }

          payload.isNew = true

          if (emittedId) {
            this.$emit('update-items', emittedId)
          }

          this.$emit('update-item', payload)
          this.$toast.add({severity: 'success', detail: this.$t('Data saved'), life: this.settings.toastLife})
          this.close()
        } else if (data?.found_items) {
          this.foundItems = data.found_items
          this.openItemExistsModal()
        } else if (data?.error) {
          this.$toast.add({severity: 'error', summary: data.error.summary, detail: data.error.detail, life: this.settings.toastLife})
        } else {
          this.showUnknownError()
        }
      } catch (err) {
        this.showError(err)
      } finally {
        this.dataIsSending = false
        this.disableSaveButton = false
      }
    },

    close() {
      this.$emit('close')
      this.submitted = false
    },
  },
}
</script>

<style scoped lang="scss">
.p-button-text {
  width: auto;
}
.radio-button-label {
  padding: 6px;
  margin-left: -2px;
  cursor: pointer;
}

.section-divider {
  display: flex;
  align-items: center;
  //gap: 8px;
}

.section-divider__hr {
  flex: 1;
  //border: none;
  //border-top: 1px solid #e5e7eb;
  margin: 0 0 0 12px;
}
</style>